/**
 * GameViewer — one live slot: the GL canvas + HUD canvas (both transferred to the worker as
 * OffscreenCanvases) and the overlay chrome (title, developer, verbs, like/tip rail, score,
 * state badge). A transferred canvas can never be reused, so this component is keyed by
 * GameHost and creates fresh elements every time.
 */
import { Show, createEffect, createSignal, onMount } from 'solid-js';
import type { GameMeta, HostState } from '../lib/protocol';
import { GameHost } from '../lib/GameHost';
import type { SlotHost } from './Carousel';
import { shareCard } from '../lib/share';
import { openUrlFor } from '../api/cards';
import { sourceOf } from '../lib/WebCardHost';
import { logUi } from '../api/ui';

/** The one word shown under a card: the first verb that describes the thing, not how it got here. */
export function plainWord(verbs: readonly string[] | undefined): string | null {
  if (!verbs?.length) return null;
  const skip = new Set(['linked', 'upload', 'uploaded']);
  return verbs.find((v) => !skip.has(v)) ?? verbs[0];
}

export interface GameViewerProps {
  host: SlotHost;
  meta: GameMeta;
  state: HostState;
  active: boolean;
  onResume: () => void;
  toast: (message: string) => void;
  /** Stamped into the share link as the referrer, so this user is credited for the tap. */
  userId: string;
  /** Open the comments on this card. */
  onComments: (meta: GameMeta) => void;
  /** Live comment count for the rail badge (the registry's number, updated as people post). */
  commentCount: number;
  /** Whether this person has liked the card (kept on their profile). */
  liked: boolean;
  /** The heart was tapped: save it (or un-save it) for this person. */
  onLike: (meta: GameMeta, liked: boolean) => void;
}

export function GameViewer(props: GameViewerProps) {
  let canvas!: HTMLCanvasElement;
  let hud!: HTMLCanvasElement;
  let frame!: HTMLIFrameElement;
  const [liked, setLiked] = createSignal(props.liked);
  createEffect(() => setLiked(props.liked));
  const [score, setScore] = createSignal(0);
  const isWeb = () => props.meta.kind === 'web';
  // A linked site is never swept idle (Carousel), so it never needs the tap-to-resume badge;
  // it can still read 'paused' for a beat around a tab hide, and then resumes on its own.
  const isExternal = () => sourceOf(props.meta) === 'external';
  /** The site itself (or the page a platform card was pasted from): where "Open" goes. */
  const openUrl = () => openUrlFor(props.meta);

  onMount(() => {
    // A wasm card gets two OffscreenCanvases transferred to its worker; a web card gets an
    // iframe. Both then load through the same lifecycle.
    if (props.host instanceof GameHost) props.host.attachCanvases(canvas, hud);
    else props.host.attachFrame(frame);
    props.host.on('score', (s) => setScore(s as number));
    props.host.load().catch(() => {
      /* surfaced via the crashed state badge */
    });
  });

  const toggleLike = () => {
    const v = !liked();
    setLiked(v);
    props.host.liked = v;
    navigator.vibrate?.(8);
    props.onLike(props.meta, v);
    logUi(v ? 'like' : 'unlike', props.meta.id);
  };
  const share = async () => {
    const how = await shareCard({ slug: props.meta.id, title: props.meta.title, referrerId: props.userId });
    // A share that reached the sheet or the clipboard is a press; a cancelled sheet is not.
    if (how === 'shared' || how === 'copied') logUi('share', props.meta.id);
    if (how === 'copied') props.toast('Link copied — it opens straight into this card');
    else if (how === 'unavailable') props.toast('Could not share from this browser');
    // 'shared' and 'cancelled' need no toast: the OS sheet already said what happened.
  };

  return (
    <div class="game" data-state={props.state}>
      {/* The surface is the card's; nothing of ours paints over it except the side rail. */}
      <div class="surface">
      <Show
        when={isWeb()}
        fallback={
          <>
            <canvas ref={canvas} class="gl" aria-label={`${props.meta.title} game surface`} />
            <canvas ref={hud} class="hud" aria-hidden="true" />
          </>
        }
      >
        {/* sandbox/src are set by WebCardHost.load(), not here, so the frame cannot navigate
            before the host has locked it down. */}
        <iframe ref={frame} class="webcard" title={props.meta.title} />
      </Show>

      <div class="rail" data-ui>
        <Show when={score() !== 0}>
          <div class="score" aria-label="score">
            {score().toLocaleString()}
          </div>
        </Show>
        <button type="button" classList={{ liked: liked() }} aria-pressed={liked()} aria-label="Like" onClick={toggleLike}>
          <svg viewBox="0 0 24 24" fill={liked() ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="2" stroke-linejoin="round">
            <path d="M12 21s-7.5-4.6-9.5-9.2C1.2 8.6 3 5 6.6 5c2 0 3.4 1.1 4.2 2.3h2.4C14 6.1 15.4 5 17.4 5 21 5 22.8 8.6 21.5 11.8 19.5 16.4 12 21 12 21z" />
          </svg>
        </button>
        <span class="label">{liked() ? 'Liked' : 'Like'}</span>
        <button type="button" aria-label={`Comments (${props.commentCount})`} data-testid="comments" onClick={() => props.onComments(props.meta)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 5h16v11H9l-5 4z" />
          </svg>
          <Show when={props.commentCount > 0}>
            <span class="badge" data-testid="comments-count">{props.commentCount > 99 ? '99+' : props.commentCount}</span>
          </Show>
        </button>
        <span class="label">Comments</span>
        {/* The card is a place you can go: the site itself, in a new tab, with no hold on it. */}
        <Show when={openUrl()}>
          {(u) => (
            <>
              <a class="open" href={u()} target="_blank" rel="noopener noreferrer" aria-label="Open this site in a new tab" data-testid="open-site">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
                </svg>
              </a>
              <span class="label">Open</span>
            </>
          )}
        </Show>
        <button type="button" aria-label="Share this card" data-testid="share" onClick={share}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3v12M7 8l5-5 5 5M5 14v5a2 2 0 002 2h10a2 2 0 002-2v-5" />
          </svg>
        </button>
        <span class="label">Share</span>
      </div>

      <Show when={props.state === 'loading' || props.state === 'idle'}>
        <div class="state-badge" aria-live="polite">
          <div class="card">
            <div class="spinner" />
            <p>{isWeb() ? 'Loading' : 'Compiling'} {props.meta.title}…</p>
          </div>
        </div>
      </Show>
      <Show when={props.state === 'paused' && props.active && !isExternal()}>
        <div class="state-badge">
          <button type="button" class="card" data-ui onClick={props.onResume} style={{ cursor: 'pointer' }}>
            <h3>Paused</h3>
            <p>Tap to keep playing</p>
          </button>
        </div>
      </Show>
      <Show when={props.state === 'ready' && !props.active}>
        <div class="state-badge">
          <div class="card">
            <h3>Ready</h3>
            <p>{isWeb() ? 'Loaded — BLITZ to open' : 'Compiled and paused at tick 0 — BLITZ to play'}</p>
          </div>
        </div>
      </Show>
      <Show when={props.state === 'crashed'}>
        <div class="state-badge">
          <div class="card">
            <h3>This card crashed</h3>
            <p>
              {props.host.lastError?.phase}: <code>{props.host.lastError?.message}</code>
            </p>
            <p>Press BLITZ for the next one.</p>
          </div>
        </div>
      </Show>
      </div>

      {/* The meta strip: below the surface, never over it. */}
      <div class="overlay">
        <div class="meta">
          <h2 class="title">{props.meta.title}</h2>
          <div class="developer">@{props.meta.developer.replace(/\s+/g, '').toLowerCase()}</div>
          {/* One plain word for what this is. The verbs stay in the data; a row of chips read
              as a builder's tag cloud, not as a label. */}
          <Show when={plainWord(props.meta.verbs)}>
            {(w) => (
              <div class="verbs">
                <span class="word" data-testid="card-word">{w()}</span>
              </div>
            )}
          </Show>
        </div>
      </div>
    </div>
  );
}
